import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './assets/styles/toast.css';

// Auth
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import { Login } from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import SetPassword from './pages/auth/SetPassword';
import EmailVerification from './pages/auth/EmailVerification';

// Pages
import LandingPage from './pages/LandingPage';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { DashboardTabs } from './pages/dashboard/DashboardTabs';
import Projects from './pages/projects/Projects';
import ProjectDetail from './pages/projects/ProjectDetail';
import Inspections from './pages/inspections/Inspections';
import TaskExecution from './pages/inspections/TaskExecution';
import Incidents from './pages/incidents/Incidents';
import Team from './pages/team/Team';
import Reports from './pages/reports/Reports';
import MyTasks from './pages/tasks/MyTasks';
import MonitoringTasks from './pages/monitoring/MonitoringTasks';
import DailyLog from './pages/monitoring/DailyLog';
import MonitoringParameters from './pages/monitoring/MonitoringParameters';
import MonitoringTrends from './pages/monitoring/MonitoringTrends';
import CategoryOverview from './pages/category/CategoryOverview';
import RiskAssessments from './pages/risk-assessment/RiskAssessments';
import Settings from './pages/settings/Settings';
import EIACertificates from './pages/eia/EIACertificates';
import EIACertificateDetail from './pages/eia/EIACertificateDetail';
import PlatformOrganisations from './pages/admin/PlatformOrganisations';
import Help from './pages/help/Help';
import PublicFormsList from './pages/forms/PublicFormsList';
import FormSubmissions from './pages/forms/FormSubmissions';
import PublicFormFill from './pages/forms/PublicFormFill';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<SetPassword />} />
          <Route path="/verify-email" element={<EmailVerification />} />

          {/* Public route - anonymous/no-login form fill (see backend
              routes/public.js). Deliberately outside ProtectedRoute/
              DashboardLayout - the person filling this in never logs in. */}
          <Route path="/public/forms/:slug" element={<PublicFormFill />} />
          
          {/* Protected routes - Dashboard */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<DashboardTabs />} />
          </Route>
          
          {/* Protected routes - Projects */}
          <Route path="/projects" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Projects />} />
            <Route path=":id" element={<ProjectDetail />} />
          </Route>
          
          {/* Protected routes - Inspections */}
          <Route path="/inspections" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Inspections />} />
            <Route path=":taskId/execute" element={<TaskExecution />} />
          </Route>
          
          {/* Protected routes - Incidents */}
          <Route path="/incidents" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Incidents />} />
          </Route>
          
          {/* Protected routes - Monitoring */}
          <Route path="/monitoring" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<MonitoringTasks />} />
            <Route path="tasks" element={<MonitoringTasks />} />
            <Route path="records" element={<DailyLog />} />
            <Route path="records/new" element={<DailyLog />} />
            <Route path="parameters" element={<MonitoringParameters />} />
            <Route path="parameters/new" element={<MonitoringParameters />} />
            <Route path="trends" element={<MonitoringTrends />} />
          </Route>
          
          {/* Protected routes - Team */}
          <Route path="/team" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Team />} />
          </Route>
          
          {/* Protected routes - Reports */}
          <Route path="/reports" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Reports />} />
          </Route>
          
          {/* Protected routes - My Tasks */}
          <Route path="/my-tasks" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<MyTasks />} />
          </Route>

          {/* Protected routes - Category Overview (dashboard shortcuts land here) */}
          <Route path="/category" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route path=":categorySlug" element={<CategoryOverview />} />
          </Route>

          {/* Protected routes - Risk Assessment Matrix (dedicated tool, not the
              generic Category Overview - see /category/riskAssessment removal) */}
          <Route path="/risk-assessment" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<RiskAssessments />} />
          </Route>

          {/* Protected routes - Settings */}
          <Route path="/settings" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Settings />} />
          </Route>

          {/* Protected routes - EIA Certificates & Conditions */}
          <Route path="/eia" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route path="certificates" element={<EIACertificates />} />
            <Route path="certificates/:id" element={<EIACertificateDetail />} />
          </Route>

          {/* Protected routes - Platform Admin (super_admin only) */}
          <Route path="/admin" element={
            <ProtectedRoute requiredRoles={['super_admin']}>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route path="organisations" element={<PlatformOrganisations />} />
          </Route>

          {/* Protected routes - Help */}
          <Route path="/help" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Help />} />
          </Route>

          {/* Protected routes - Forms (shareable public intake forms) */}
          <Route path="/forms" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }>
            <Route index element={<PublicFormsList />} />
            <Route path=":id/submissions" element={<FormSubmissions />} />
          </Route>
        </Routes>
        <ToastContainer 
          position="top-right"
          autoClose={3000}
          hideProgressBar
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable={false}
          pauseOnHover
          transition={Slide}
          limit={3}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
